import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import {
  type CheckoutPlan,
  getPlanLabel,
  getStatusLabel,
  isPremiumUser,
} from '@/lib/subscription';
import { toast } from 'sonner';

export function useSubscription() {
  const { isAdmin } = useAuth();
  const { profile, isLoading } = useProfile();
  const queryClient = useQueryClient();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const isPremium = useMemo(
    () => isPremiumUser(profile, isAdmin),
    [profile, isAdmin],
  );

  const planLabel = useMemo(
    () => (isPremium ? getPlanLabel(profile?.subscription_plan ?? null) : 'Gratuito'),
    [isPremium, profile?.subscription_plan],
  );

  const statusLabel = useMemo(
    () => getStatusLabel(profile?.subscription_status ?? null),
    [profile?.subscription_status],
  );

  const refreshSubscription = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['profile'] });
  }, [queryClient]);

  /** Poll profile until Stripe webhook sets premium (post-checkout). */
  const waitForPremiumActivation = useCallback(
    async (options?: { maxAttempts?: number; intervalMs?: number }) => {
      const maxAttempts = options?.maxAttempts ?? 8;
      const intervalMs = options?.intervalMs ?? 1500;

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return false;

      if (isAdmin) {
        await refreshSubscription();
        return true;
      }

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const { data, error } = await supabase
          .from('profiles')
          .select('subscription_tier')
          .eq('id', user.id)
          .single();

        if (!error && data?.subscription_tier === 'premium') {
          await refreshSubscription();
          return true;
        }

        if (attempt < maxAttempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, intervalMs));
        }
      }

      await refreshSubscription();
      return false;
    },
    [isAdmin, refreshSubscription],
  );

  const startCheckout = useCallback(
    async (plan: CheckoutPlan) => {
      setCheckoutLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke('create-checkout-session', {
          body: { plan },
        });

        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        if (!data?.url) throw new Error('No se recibió URL de pago');

        window.location.href = data.url;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Error al iniciar el pago';
        toast.error(message);
      } finally {
        setCheckoutLoading(false);
      }
    },
    [],
  );

  const openPortal = useCallback(async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-portal-session');

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (!data?.url) throw new Error('No se recibió URL del portal');

      window.location.href = data.url;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al abrir el portal';
      toast.error(message);
    } finally {
      setPortalLoading(false);
    }
  }, []);

  return {
    profile,
    isLoading,
    isAdmin,
    isPremium,
    planLabel,
    statusLabel,
    tier: profile?.subscription_tier ?? 'free',
    expiresAt: profile?.subscription_expires_at ?? null,
    statementImportCount: profile?.successful_statement_imports ?? 0,
    checkoutLoading,
    portalLoading,
    startCheckout,
    openPortal,
    refreshSubscription,
    waitForPremiumActivation,
  };
}
